import React, { useContext, useEffect, useState } from "react";
import { faMinus, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Switch from "react-switch";
import { Button, Card, CardBody, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { PostData } from "../../../../services/FetchData";
import { toast } from "react-toastify";
import { ProductContext } from "../../../../store";
import { Link } from "react-router-dom";
import DetailSkeleton from "../../../../components/Skeleton/DetailSkeleton";
import IndexDropdown from "../../../../components/Drupal/IndexDropdown";

const DRUPAL_BASE = "ms3";

const DRUPAL_SECTION_TYPES = [
	{ key: "products", label: "Products" },
	{ key: "content", label: "Content" },
	{ key: "product_taxonomy", label: "Product taxonomy" },
];

// APIs: getAutoCompleteConfig, saveAutoComplete, toggleAutoComplete (same path names for all platforms)
const PLATFORMS = { MAGENTO: "magento", SHOPIFY: "shopify", BIGCOMMERCE: "bigcommerce", WOOCOMMERCE: "woocommerce", DRUPAL: "drupal" };
const PLATFORM_PREFIX = { [PLATFORMS.MAGENTO]: "magento", [PLATFORMS.SHOPIFY]: "shopify", [PLATFORMS.BIGCOMMERCE]: "bigcommerce", [PLATFORMS.DRUPAL]: "drupal", [PLATFORMS.WOOCOMMERCE]: "woocommerce" };

const getAutoCompleteApiConfig = (platform) => {
	const base = platform === PLATFORMS.MAGENTO ? "ms2" : "ms3";
	const prefix = PLATFORM_PREFIX[platform];
	const path = (endpoint) => `api/v1/${prefix}/${endpoint}`;
	return {
		base,
		getConfigPath: () => path("getAutoCompleteConfig"),
		togglePath: () => path("toggleAutoComplete"),
		savePath: () => path("saveAutoComplete"),
	};
};

const AutoComplete = () => {
	const { currentWebsiteId, websiteData, websiteLoader, userWebsitePlan } = useContext(ProductContext);
	const [loader, setLoader] = useState(true);
	const [saveLoader, setSaveLoader] = useState(false);
	const [toggleLoader, setToggleLoader] = useState(false);
	const [autoCompleteConfig, setAutoCompleteConfig] = useState(null);
	const [indexConfig, setIndexConfig] = useState({});
	const [showInfo, setShowInfo] = useState(false);
	// Bigcommerce additional fields
	const [availableFieldOptions, setAvailableFieldOptions] = useState([]);
	const [additionalFieldsEnabled, setAdditionalFieldsEnabled] = useState(false);
	const [additionalFields, setAdditionalFields] = useState([]);
	const [requiredFields, setRequiredFields] = useState({});
	const [conversionTypes, setConversionTypes] = useState({});
	const [measurementUnits, setMeasurementUnits] = useState({});

	const platform = websiteData?.[currentWebsiteId]?.websiteType;
	const isMagento = platform === PLATFORMS.MAGENTO;
	const isShopify = platform === PLATFORMS.SHOPIFY;
	const isBigcommerce = platform === PLATFORMS.BIGCOMMERCE;
	const isWooCommerce = platform === PLATFORMS.WOOCOMMERCE;
	const isDrupal = platform === PLATFORMS.DRUPAL;
	const isMs3 = isShopify || isBigcommerce || isWooCommerce || isDrupal;

	useEffect(() => {
		if (!currentWebsiteId || !platform) return;
		getAutoCompleteConfig();
	}, [currentWebsiteId, platform]);

	const getAutoCompleteConfig = () => {
		if (!platform) return;
		setLoader(true);
		if (isDrupal) {
			PostData(DRUPAL_BASE, "api/v1/drupal/autocomplete/config", { websiteId: currentWebsiteId })
				.then((res) => {
					const ac = res.autoCompleteConfig || {};
					const display = ac.autocompleteDisplay || {};
					const raw = ac.autocompleteIndexes || [];
					const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, "_");
					const slots = {
						products: raw.find((s) => ["products", "product"].includes(norm(s.section))) || { section: "Products", index_id: "", limit: 5 },
						content: raw.find((s) => norm(s.section) === "content") || { section: "Content", index_id: "", limit: 5 },
						product_taxonomy: raw.find((s) => ["product_taxonomy", "product taxonomy"].includes(norm(s.section))) || { section: "Product taxonomy", index_id: "", limit: 5 },
					};
					const autocompleteIndexes = [slots.products, slots.content, slots.product_taxonomy];
					setAutoCompleteConfig({
						status: ac.status,
						selectors: ac.selectors || [],
						autocompleteIndexes,
						autocompleteDisplay: {
							showPrice: display.showPrice !== false,
							outOfStock: Boolean(display.outOfStock),
							showDescription: Boolean(display.showDescription),
							descriptionMaxLines: Math.max(1, parseInt(display.descriptionMaxLines, 10) || 2),
							showVendor: Boolean(display.showVendor),
							enableQuerySuggestion: Boolean(display.enableQuerySuggestion),
							noOfQuerySuggestion: Math.max(1, Math.min(10, parseInt(display.noOfQuerySuggestion, 10) || 5)),
							highlightSearchTerm: display.highlightSearchTerm !== false,
							seeAllButton: display.seeAllButton !== false,
							groupByField: Boolean(display.groupByField),
						},
					});
					setIndexConfig({ indexIds: (res.indexConfig || {}).indexIds || [] });
				})
				.catch((err) => toast.error(err?.message || "Failed to load config"))
				.finally(() => setLoader(false));
			return;
		}
		const { base, getConfigPath } = getAutoCompleteApiConfig(platform);
		PostData(base, getConfigPath(), { websiteId: currentWebsiteId }).then((result) => {
			if (result === "Invalid" || result === "undefined") {
				setLoader(false);
				return;
			}
			if (result.status === "success") {
				setAutoCompleteConfig(result.data.autoCompleteConfig);
				if (result.data.indexConfig) setIndexConfig(result.data.indexConfig || {});
				if (isBigcommerce && result.data.additionalFields) {
					const fields = result.data.additionalFields || [];
					setAvailableFieldOptions(
						fields.map((f) => ({
							value: f,
							content: f.replace(/\./g, " ").split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
						}))
					);
					const fieldsArray = result.data.autoCompleteConfig?.additionalFields || [];
					setAdditionalFields(fieldsArray.map((f) => f.field || ""));
					const req = {}, conv = {}, units = {};
					fieldsArray.forEach((f, i) => {
						req[i] = f.required || false;
						conv[i] = f.type || "String";
						if (f.type === "Number" && f.unit) units[i] = f.unit;
					});
					setRequiredFields(req);
					setConversionTypes(conv);
					setMeasurementUnits(units);
					setAdditionalFieldsEnabled(fieldsArray.length > 0);
				}
			} else {
				toast.error(result.message?.message || result.message);
			}
			setLoader(false);
		});
	};

	// Toggle payload: Magento sends flipped status; Bigcommerce/WooCommerce (old files) send websiteId from config + current status; Shopify sends currentWebsiteId + current status.
	const toggleAutoComplete = () => {
		if (!autoCompleteConfig) return;
		setToggleLoader(true);
		if (isDrupal) {
			const newStatus = !autoCompleteConfig.status;
			PostData(DRUPAL_BASE, "api/v1/drupal/autocomplete/toggle", { websiteId: currentWebsiteId, status: newStatus })
				.then(() => {
					setAutoCompleteConfig((c) => (c ? { ...c, status: newStatus } : c));
					toast.success(newStatus ? "Autocomplete enabled" : "Autocomplete disabled");
				})
				.catch((err) => toast.error(err?.message || "Failed to update"))
				.finally(() => setToggleLoader(false));
			return;
		}
		const { base, togglePath } = getAutoCompleteApiConfig(platform);
		let payload;
		if (isMagento) {
			payload = { websiteId: autoCompleteConfig.websiteId, status: !autoCompleteConfig.status };
		} else if (isBigcommerce || isWooCommerce) {
			payload = { websiteId: autoCompleteConfig.websiteId, status: autoCompleteConfig.status };
		} else {
			payload = { websiteId: currentWebsiteId, status: autoCompleteConfig.status };
		}
		PostData(base, togglePath(), payload).then((result) => {
			if (result !== "Invalid" && result !== "undefined" && result?.status === "success") {
				const newStatus = isMagento ? !autoCompleteConfig.status : !autoCompleteConfig.status;
				setAutoCompleteConfig({ ...autoCompleteConfig, status: newStatus });
				// For WooCommerce/Bigcommerce use new status for toast so message always matches toggle state (backend message was inverted)
				const newStatusFromApi = result?.data?.status;
				const toastMessage = (isWooCommerce || isBigcommerce) && typeof newStatusFromApi === "boolean"
					? (newStatusFromApi ? "Autocomplete has been added to your store front" : "Autocomplete has been removed from your store front")
					: (result.message || (newStatus ? "Autocomplete enabled" : "Autocomplete disabled"));
				toast.success(toastMessage);
			} else {
				toast.error(result?.message?.message || result?.message || "Failed to update");
			}
			setToggleLoader(false);
		});
	};

	const saveAutoCompleteConfig = () => {
		if (!autoCompleteConfig) return;
		setSaveLoader(true);
		if (isDrupal) {
			const selectors = Array.isArray(autoCompleteConfig.selectors) ? autoCompleteConfig.selectors : (autoCompleteConfig.selectors || "").toString().split(",").map((s) => s.trim()).filter(Boolean);
			const sections = (autoCompleteConfig.autocompleteIndexes || []).filter((s) => s && String(s.index_id || "").trim() !== "");
			PostData(DRUPAL_BASE, "api/v1/drupal/autocomplete/save", {
				websiteId: currentWebsiteId,
				status: autoCompleteConfig.status,
				selectors,
				autocompleteIndexes: sections,
				autocompleteDisplay: autoCompleteConfig.autocompleteDisplay || {},
			})
				.then(() => toast.success("Saved"))
				.catch((err) => toast.error(err?.message || "Failed to save"))
				.finally(() => setSaveLoader(false));
			return;
		}
		const { base, savePath } = getAutoCompleteApiConfig(platform);
		let payload;
		if (isMagento) {
			payload = { ...autoCompleteConfig };
		} else if (isShopify) {
			payload = {
				cssSelector: autoCompleteConfig.cssSelector,
				noOfProducts: autoCompleteConfig.noOfProducts,
				noOfCollections: autoCompleteConfig.noOfCollections,
				colors: autoCompleteConfig.colors || [],
				seeAllButton: autoCompleteConfig.seeAllButton,
				showVendor: autoCompleteConfig.showVendor,
				outOfStock: autoCompleteConfig.outOfStock,
				noOfPages: autoCompleteConfig.noOfPages,
				noOfBlogs: autoCompleteConfig.noOfBlogs || 0,
				enableQuerySuggestion: autoCompleteConfig.enableQuerySuggestion,
				noOfQuerySuggestion: autoCompleteConfig.noOfQuerySuggestion,
				highlightSearchTerm: autoCompleteConfig.highlightSearchTerm,
				showPrice: autoCompleteConfig.showPrice,
				showDescription: autoCompleteConfig.showDescription,
				descriptionMaxLines: autoCompleteConfig.descriptionMaxLines,
				showSKU: autoCompleteConfig.showSKU,
				infiniteScroll: autoCompleteConfig.infiniteScroll,
				websiteId: currentWebsiteId,
			};
		} else if (isBigcommerce) {
			const nonEmpty = (additionalFields || []).filter((f) => f !== "");
			const formatted = nonEmpty.map((field, i) => {
				const d = { field, required: requiredFields[i] || false, type: conversionTypes[i] || "String" };
				if (d.type === "Number") d.unit = measurementUnits[i] || "";
				return d;
			});
			payload = { ...autoCompleteConfig, websiteId: autoCompleteConfig.websiteId, additionalFields: formatted };
		} else {
			payload = { ...autoCompleteConfig, websiteId: autoCompleteConfig.websiteId };
		}
		PostData(base, savePath(), payload).then((result) => {
			if (result !== "Invalid" && result !== "undefined" && result?.status === "success") {
				toast.success(result.message || "Saved");
			} else {
				toast.error(result?.message?.message || result?.message || "Failed to save");
			}
			setSaveLoader(false);
		});
	};

	const changeAutoCompleteConfig = (key, value) => {
		setAutoCompleteConfig((prev) => (prev ? { ...prev, [key]: value } : null));
	};

	const numRow = (label, key, min = 0, help, opts = {}) => {
		const { source, max } = opts;
		const data = source ? (autoCompleteConfig[source] || {}) : autoCompleteConfig;
		const value = data[key];
		const setValue = (v) => {
			const num = parseInt(v, 10);
			if (isNaN(num)) return;
			const clamped = max != null ? Math.min(max, Math.max(min, num)) : Math.max(min, num);
			if (source) changeAutoCompleteConfig(source, { ...data, [key]: clamped });
			else changeAutoCompleteConfig(key, clamped);
		};
		const current = value != null ? Number(value) : (opts.default != null ? opts.default : min);
		const displayVal = max != null ? Math.min(max, Math.max(min, current)) : Math.max(min, current);
		return (
			<Row key={key} className="mb-4 align-items-center">
				<Col xs={12} md={4}><label><h4>{label}</h4></label></Col>
				<Col xs={12} md={6}>
					<div className="input-container">
						<Button type="button" className="btn-minus" onClick={() => setValue(displayVal - 1)}><FontAwesomeIcon icon={faMinus} /></Button>
						<Input type="number" value={displayVal} onChange={(e) => setValue(e.target.value)} min={min} max={max} className="form-control" />
						<Button type="button" className="btn-plus" onClick={() => setValue(displayVal + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
					</div>
					{help && <div className="buttonContext"><p className="mb-0 text-muted">{help}</p></div>}
				</Col>
			</Row>
		);
	};

	const checkRow = (label, key, help) => (
		<Row key={key} className="mb-4 align-items-center">
			<Col xs={12} md={4}><label><h4>{label}</h4></label></Col>
			<Col xs={12} md={6}>
				<div className="checkboxContainer">
					<Input type="checkbox" className="checkboxContainerInput" checked={!!autoCompleteConfig[key]} onChange={() => changeAutoCompleteConfig(key, !autoCompleteConfig[key])} />
				</div>
				{help && <div className="buttonContext"><p className="mb-0">{help}</p></div>}
			</Col>
		</Row>
	);

	// Bigcommerce additional fields handlers
	const handleAdditionalFieldChange = (value, index) => {
		const newFields = [...additionalFields];
		newFields[index] = value;
		setAdditionalFields(newFields);
	};
	const handleRequiredFieldChange = (index, value) => {
		setRequiredFields((prev) => ({ ...prev, [index]: value }));
	};
	const handleConversionTypeChange = (index, value) => {
		setConversionTypes((prev) => ({ ...prev, [index]: value }));
	};
	const handleMeasurementUnitChange = (index, value) => {
		setMeasurementUnits((prev) => ({ ...prev, [index]: value }));
	};
	const addNewField = () => {
		if (additionalFields.length < 4) {
			setAdditionalFields([...additionalFields, ""]);
			setRequiredFields((prev) => ({ ...prev, [additionalFields.length]: false }));
			setConversionTypes((prev) => ({ ...prev, [additionalFields.length]: "String" }));
		}
	};
	const removeField = (index) => {
		const newFields = additionalFields.filter((_, i) => i !== index);
		setAdditionalFields(newFields);
		const reindex = (obj) => {
			const out = {};
			Object.entries(obj).forEach(([k, v]) => { const i = parseInt(k, 10); if (i !== index) out[i > index ? i - 1 : i] = v; });
			return out;
		};
		setRequiredFields(reindex(requiredFields));
		setConversionTypes(reindex(conversionTypes));
		setMeasurementUnits(reindex(measurementUnits));
	};

	if (websiteLoader || !websiteData?.[currentWebsiteId]) return <DetailSkeleton />;
	if (!platform || !["magento", "shopify", "bigcommerce", "woocommerce", "drupal"].includes(platform)) return <DetailSkeleton />;
	if (loader || !autoCompleteConfig) return <DetailSkeleton />;

	return (
		<div className="search-options-card">
			<div className="card-header">
				<div className="title-text">
					<h3>Autocomplete</h3>
					<span>Display multiple panels of results from different data sources as soon as they start typing.</span>
					{isShopify && (
						<>
							<span> Note: using a 2.0 theme? <a className="more-info-link text-primary cursor-pointer text-decoration-underline" onClick={() => setShowInfo(!showInfo)}>More Info</a></span>
							{showInfo && (
								<div className="info-message p-2 pt-0">
									If you're using a 2.0 theme, turn off predictive search from Shopify (e.g. uncheck &apos;Enable product suggestions&apos; in Header section) to avoid overlapping search boxes.
								</div>
							)}
						</>
					)}
				</div>
				<div className="switch-container">
					<span>{autoCompleteConfig.status ? "Enabled" : "Disabled"}</span>
					{toggleLoader ? <Spinner size="sm" /> : (
						<Switch checked={!!autoCompleteConfig.status} onChange={toggleAutoComplete} onColor="#9eb3fc" onHandleColor="#3c64f4" handleDiameter={25} uncheckedIcon={false} checkedIcon={false} boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)" activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)" height={15} width={40} className="react-switch" id="material-switch" />
					)}
				</div>
			</div>
			<div className="card-content">
				<form>
					{/* Drupal: simple form with CSS selectors + sections */}
					{isDrupal && (
						<>
							<Row className="mb-3">
								<Col xs={12}>
									<p className="text-muted small mb-0">
										Configure search fields and weights (shared with instant search):{" "}
										<Link to="/search/searchOptions/searchQueryFields">Search fields &amp; weights</Link>.
										{" "}Semantic &amp; NL search:{" "}
										<Link to="/search/searchOptions/semanticSearch">Semantic Search</Link>.
									</p>
								</Col>
							</Row>
							<Row className="mb-4 align-items-start">
								<Col xs={12} md={4}><label><h4>CSS Selectors</h4></label></Col>
								<Col xs={12} md={6}>
									<Input
										type="text"
										value={(autoCompleteConfig.selectors || []).join(", ")}
										onChange={(e) => changeAutoCompleteConfig("selectors", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
										placeholder="#cb-search-input, #search-input"
									/>
									<div className="buttonContext"><p className="text-muted mb-0">Comma-separated CSS selectors for the search input(s). Default from block: <code>#cb-search-input</code></p></div>
								</Col>
							</Row>
							<Row className="mb-4 align-items-start">
								<Col xs={12}><label><h4>Sections</h4></label></Col>
								<Col xs={12}>
									<p className="text-muted small mb-2">Select index and limit for each section. Only sections with an index selected appear in the autocomplete dropdown. Auto suggestion uses product data when Products is enabled.</p>
								</Col>
							</Row>
							{(() => {
								const sections = autoCompleteConfig.autocompleteIndexes || [];
								const padded = [
									sections[0] || { section: "Products", index_id: "", limit: 5 },
									sections[1] || { section: "Content", index_id: "", limit: 5 },
									sections[2] || { section: "Product taxonomy", index_id: "", limit: 5 },
								];
								return DRUPAL_SECTION_TYPES.map(({ key, label }, i) => {
								const s = padded[i];
								const updateSection = (field, value) => {
									const next = [...padded];
									next[i] = { ...next[i], section: label, [field]: value };
									changeAutoCompleteConfig("autocompleteIndexes", next);
								};
								return (
									<Row key={key} className="mb-3 align-items-center">
										<Col xs={12} md={3}><strong>{label}</strong></Col>
										<Col xs={12} md={5}>
											<IndexDropdown
												indexIds={indexConfig.indexIds || []}
												value={s.index_id || ""}
												onChange={(val) => updateSection("index_id", val)}
												placeholder={"Select index for " + label.toLowerCase()}
											/>
										</Col>
										<Col xs={12} md={2}>
											<div className="input-container">
												<Button type="button" className="btn-minus" onClick={() => updateSection("limit", Math.max(1, (s.limit ?? 5) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
												<Input
													type="number"
													min={1}
													max={20}
													value={s.limit ?? 5}
													onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) updateSection("limit", Math.max(1, Math.min(20, v))); }}
													placeholder="Limit"
													className="form-control"
												/>
												<Button type="button" className="btn-plus" onClick={() => updateSection("limit", Math.min(20, (s.limit ?? 5) + 1))}><FontAwesomeIcon icon={faPlus} /></Button>
											</div>
										</Col>
										<Col xs={12} md={2}><span className="text-muted small">results</span></Col>
									</Row>
								);
							});
							})()}
							{(() => {
								const productsSection = (autoCompleteConfig.autocompleteIndexes || []).find((s) => (s.section || "").toLowerCase().replace(/\s+/g, "_") === "products" || (s.section || "").toLowerCase() === "product");
								const productsEnabled = !!(productsSection && String(productsSection.index_id || "").trim());
								if (!productsEnabled) return null;
								return (
									<>
							<Row className="mb-4 mt-4"><Col xs={12}><h4>Product display options</h4></Col></Row>
							{(() => {
								const d = autoCompleteConfig.autocompleteDisplay || {};
								const setDisplay = (key, value) => changeAutoCompleteConfig("autocompleteDisplay", { ...d, [key]: value });
								return (
									<>
										<Row className="mb-4 align-items-center">
											<Col xs={12} md={4}><label><h4>Show price</h4></label></Col>
											<Col xs={12} md={6}>
												<div className="checkboxContainer">
													<Input type="checkbox" className="checkboxContainerInput" checked={d.showPrice !== false} onChange={() => setDisplay("showPrice", !d.showPrice)} />
												</div>
												<div className="buttonContext"><p className="mb-0 text-muted">Display product prices in search results.</p></div>
											</Col>
										</Row>
										<Row className="mb-4 align-items-center">
											<Col xs={12} md={4}><label><h4>Show out of stock products</h4></label></Col>
											<Col xs={12} md={6}>
												<div className="checkboxContainer">
													<Input type="checkbox" className="checkboxContainerInput" checked={!!d.outOfStock} onChange={() => setDisplay("outOfStock", !d.outOfStock)} />
												</div>
												<div className="buttonContext"><p className="mb-0 text-muted">Products marked as not in stock will show in autocomplete.</p></div>
											</Col>
										</Row>
										<Row className="mb-4 align-items-center">
											<Col xs={12} md={4}><label><h4>Show product description</h4></label></Col>
											<Col xs={12} md={6}>
												<div className="checkboxContainer">
													<Input type="checkbox" className="checkboxContainerInput" checked={!!d.showDescription} onChange={() => setDisplay("showDescription", !d.showDescription)} />
												</div>
												<div className="buttonContext"><p className="mb-0 text-muted">Display product descriptions in autocomplete results.</p></div>
											</Col>
										</Row>
										{d.showDescription && numRow("Maximum description lines", "descriptionMaxLines", 1, "Max lines for description in dropdown.", { source: "autocompleteDisplay", max: 10, default: 2 })}
										<Row className="mb-4 align-items-center">
											<Col xs={12} md={4}><label><h4>Show vendor / brand</h4></label></Col>
											<Col xs={12} md={6}>
												<div className="checkboxContainer">
													<Input type="checkbox" className="checkboxContainerInput" checked={!!d.showVendor} onChange={() => setDisplay("showVendor", !d.showVendor)} />
												</div>
												<div className="buttonContext"><p className="mb-0 text-muted">Display vendor or brand in product row.</p></div>
											</Col>
										</Row>
										<Row className="mb-4 align-items-center">
											<Col xs={12} md={4}><label><h4>Enable query suggestions</h4></label></Col>
											<Col xs={12} md={6}>
												<div className="checkboxContainer">
													<Input type="checkbox" className="checkboxContainerInput" checked={!!d.enableQuerySuggestion} onChange={() => setDisplay("enableQuerySuggestion", !d.enableQuerySuggestion)} />
												</div>
												<div className="buttonContext"><p className="mb-0 text-muted">Show suggested queries based on popular searches (from products).</p></div>
											</Col>
										</Row>
										{d.enableQuerySuggestion && numRow("Number of query suggestions", "noOfQuerySuggestion", 1, "How many suggestions to show (1–10).", { source: "autocompleteDisplay", max: 10, default: 5 })}
										<Row className="mb-4 align-items-center">
											<Col xs={12} md={4}><label><h4>Highlight search term</h4></label></Col>
											<Col xs={12} md={6}>
												<div className="checkboxContainer">
													<Input type="checkbox" className="checkboxContainerInput" checked={d.highlightSearchTerm !== false} onChange={() => setDisplay("highlightSearchTerm", d.highlightSearchTerm === false)} />
												</div>
												<div className="buttonContext"><p className="mb-0 text-muted">Highlight the search term in titles and descriptions.</p></div>
											</Col>
										</Row>
										<Row className="mb-4 align-items-center">
											<Col xs={12} md={4}><label><h4>See all button</h4></label></Col>
											<Col xs={12} md={6}>
												<div className="checkboxContainer">
													<Input type="checkbox" className="checkboxContainerInput" checked={d.seeAllButton !== false} onChange={() => setDisplay("seeAllButton", d.seeAllButton === false)} />
												</div>
												<div className="buttonContext"><p className="mb-0 text-muted">Show &quot;See all X results&quot; link per section.</p></div>
											</Col>
										</Row>
										<Row className="mb-4 align-items-center">
											<Col xs={12} md={4}><label><h4>Group by product ID</h4></label></Col>
											<Col xs={12} md={6}>
												<div className="checkboxContainer">
													<Input type="checkbox" className="checkboxContainerInput" checked={!!d.groupByField} onChange={() => setDisplay("groupByField", !d.groupByField)} />
												</div>
												<div className="buttonContext"><p className="mb-0 text-muted">Deduplicate by product_id so one result per product (e.g. one per base product when you have variations).</p></div>
											</Col>
										</Row>
									</>
								);
							})()}
									</>
								);
							})()}
							<Row className="justify-content-end mt-4">
								<Col xs="auto">
									<Button type="button" color="primary" onClick={saveAutoCompleteConfig} disabled={saveLoader}>
										{saveLoader ? <Spinner size="sm" /> : "Save"}
									</Button>
								</Col>
							</Row>
						</>
					)}

					{!isDrupal && (
						<>
					{/* CSS Selector: Shopify (single) or Bigcommerce/WooCommerce (desktop + mobile) */}
					{isShopify && (
						<Row className="mb-4 align-items-start">
							<Col xs={12} md={4}><label><h4>CSS Selector</h4></label></Col>
							<Col xs={12} md={6}>
								<Input type="text" placeholder="Enter CSS selector" value={autoCompleteConfig.cssSelector || ""} onChange={(e) => changeAutoCompleteConfig("cssSelector", e.target.value)} />
								<div className="buttonContext"><p className="text-muted mb-0">CSS Selector for the search input. You might have to change this value to an element selector that works for your theme.</p></div>
							</Col>
						</Row>
					)}
					{(isBigcommerce || isWooCommerce) && (
						<>
							<Row className="mb-4 align-items-start">
								<Col xs={12} md={4}><label><h4>Desktop CSS Selector *</h4></label></Col>
								<Col xs={12} md={6}>
									<Input type="text" value={autoCompleteConfig.desktopCssSelector || ""} onChange={(e) => changeAutoCompleteConfig("desktopCssSelector", e.target.value)} />
									<div className="buttonContext"><p className="mb-0">Desktop CSS Selector for the search input. You might have to change this value to an element selector that works for your theme. This field is required and case sensitive.</p></div>
								</Col>
							</Row>
							<Row className="mb-4 align-items-start">
								<Col xs={12} md={4}><label><h4>Mobile CSS Selector</h4></label></Col>
								<Col xs={12} md={6}>
									<Input type="text" value={autoCompleteConfig.mobileCssSelector || ""} onChange={(e) => changeAutoCompleteConfig("mobileCssSelector", e.target.value)} />
									<div className="buttonContext"><p className="mb-0">Mobile CSS Selector for the search input. Only needed if different from Desktop CSS selector</p></div>
								</Col>
							</Row>
							{autoCompleteConfig.mobileCssSelector && numRow("Max Mobile Width", "maxMobileWidth", 320, "Screen size for mobile view (px).")}
							<Row className="mb-4 align-items-start">
								<Col xs={12} md={4}><label><h4>Input placeholder</h4></label></Col>
								<Col xs={12} md={6}>
									<Input type="text" value={autoCompleteConfig.inputPlaceholder || ""} onChange={(e) => changeAutoCompleteConfig("inputPlaceholder", e.target.value)} />
									<div className="buttonContext"><p className="mb-0">Placeholder for the search input.</p></div>
								</Col>
							</Row>
						</>
					)}

					{numRow("No Of Products Shown", "noOfProducts", 1, "Number of products to show in search. Defaults to 6.")}

					{isMagento && (
						<>
							{checkRow("Enable Category", "enableCategory", "Displays category in the left column.")}
							{autoCompleteConfig.enableCategory && numRow("No Of Category Shown", "noOfCategories", 0, "Defaults to 3.")}
							{checkRow("Enable Pages", "enablePage", "Displays page in the left column.")}
							{autoCompleteConfig.enablePage && numRow("No Of Pages Shown", "noOfPages", 0, "Defaults to 2.")}
						</>
					)}
					{(isShopify || isBigcommerce || isWooCommerce || isDrupal) && (
						<>
							{numRow(isShopify ? "No Of Collections Shown" : "No Of Category Shown", "noOfCollections", 1, isShopify ? "Number of collections to show in search. Defaults to 3." : "Number of category to show in search. Defaults to 3.")}
							{(isBigcommerce || indexConfig?.index_pages === "true") && numRow("No Of Pages Shown", "noOfPages", 1, "Number of pages to show in search. Defaults to 3.")}
							{(isBigcommerce || indexConfig?.index_blogs === "true") && numRow("No Of Blogs Shown", "noOfBlogs", 1, "Number of blogs to show in search. Defaults to 2.")}
						</>
					)}

					{checkRow("Enable Query Suggestions", "enableQuerySuggestion", "Displays Query Suggestions in the left column.")}
					{autoCompleteConfig.enableQuerySuggestion && numRow("Number Of Suggestions Shown", "noOfQuerySuggestion", 1)}

					{checkRow("Show Price", "showPrice", "Display product prices in search results.")}
					{checkRow("Show Out Of Stock Products", "outOfStock", "Products marked as not in stock will show up in search results.")}
					{checkRow("Show Product Description", "showDescription", "Display product descriptions in search results.")}
					{(autoCompleteConfig.showDescription || isBigcommerce) && numRow("Maximum Description Lines", "descriptionMaxLines", 1, "Maximum number of description lines to display in autocomplete results.")}
					{checkRow("Show SKU", "showSKU")}

					{(isShopify || isBigcommerce || isWooCommerce || isDrupal) && (
						<>
							{checkRow("Highlight search term", "highlightSearchTerm")}
							{checkRow("Show vendor", "showVendor")}
						</>
					)}
					{checkRow("See All Button", "seeAllButton")}

					{isMagento && numRow("Minimum query length", "minimumCharLength", 0, "Min characters before request. 0 = first keystroke.")}

					{/* Bigcommerce additional fields */}
					{isBigcommerce && userWebsitePlan?.some((p) => p.planId === 3) && (
						<>
							<Row className="mb-4 align-items-start">
								<Col xs={12} md={4}><label><h4>Additional Fields</h4></label></Col>
								<Col xs={12} md={6}>
									<Switch checked={additionalFieldsEnabled} disabled={!availableFieldOptions.length} onChange={() => { setAdditionalFieldsEnabled(!additionalFieldsEnabled); if (!additionalFieldsEnabled && additionalFields.length === 0) setAdditionalFields([""]); else if (additionalFieldsEnabled) setAdditionalFields([]); }} />
									<div className="buttonContext"><p className="mb-0">Add up to 4 additional fields to display in autocomplete results.</p></div>
								</Col>
							</Row>
							{additionalFieldsEnabled && additionalFields.map((field, index) => (
								<Card key={index} className="mb-4 border-0 shadow-sm">
									<CardBody>
										<Row className="align-items-center">
											<Col lg={4}>
												<FormGroup>
													<Input type="select" value={field} onChange={(e) => handleAdditionalFieldChange(e.target.value, index)}>
														<option value="">Select a field</option>
														{availableFieldOptions.filter((o) => !additionalFields.includes(o.value) || o.value === field).map((o) => <option key={o.value} value={o.value}>{o.content}</option>)}
													</Input>
												</FormGroup>
											</Col>
											<Col lg={2} className="d-flex justify-content-center"><Input type="checkbox" checked={!!requiredFields[index]} onChange={() => handleRequiredFieldChange(index, !requiredFields[index])} /></Col>
											<Col lg={3}>
												<Input type="select" value={conversionTypes[index] || "String"} onChange={(e) => handleConversionTypeChange(index, e.target.value)}><option value="String">String</option><option value="Number">Number</option></Input>
											</Col>
											<Col lg={2}>{conversionTypes[index] === "Number" && <Input type="text" placeholder="Unit" value={measurementUnits[index] || ""} onChange={(e) => handleMeasurementUnitChange(index, e.target.value)} />}</Col>
											<Col lg={1}>
												{index === additionalFields.length - 1 && additionalFields.length < 4 && <Button color="success" size="sm" onClick={addNewField}><FontAwesomeIcon icon={faPlus} /></Button>}
												{additionalFields.length > 1 && <Button color="danger" size="sm" onClick={() => removeField(index)}><FontAwesomeIcon icon={faTimes} /></Button>}
											</Col>
										</Row>
									</CardBody>
								</Card>
							))}
						</>
					)}

					<Row className="justify-content-end">
						<Col xs="auto">
							<Button type="button" color="primary" onClick={saveAutoCompleteConfig} disabled={saveLoader}>
								{saveLoader ? <Spinner size="sm" /> : "Save"}
							</Button>
						</Col>
					</Row>
						</>
					)}
				</form>
			</div>
		</div>
	);
};

export default AutoComplete;
